import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { fetchPostBySlug, fetchRelatedPosts, BlogPost } from '@/data/blog-posts';

function RelatedPostCard({ post }: { post: BlogPost }) {
  return (
    <div className="bg-[#111111] p-8 group">
      <p className="text-xs text-[#888888] uppercase tracking-widest mb-4">{post.category}</p>
      <h3 className="text-sm font-bold text-[#f5f5f5] leading-snug group-hover:text-[#E8651A] transition-colors mb-4">
        {post.title}
      </h3>
      <Link
        to={`/blog/${post.slug}`}
        className="text-xs text-[#E8651A] hover:text-[#C9520E] transition-colors"
      >
        Read →
      </Link>
    </div>
  );
}

export const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | undefined>();
  const [related, setRelated] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchPostBySlug(slug ?? '').then(p => {
      setPost(p);
      if (p) {
        document.title = `${p.title} — Custom Group`;
        fetchRelatedPosts(p, 3).then(setRelated);
      }
      setLoading(false);
    });
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#E8651A] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5]">
      {/* Nav */}
      <nav className="bg-[#0a0a0a] border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo.svg" alt="Custom Group.Tech" className="h-9 w-auto" />
            </Link>
            <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#888888]">
              <Link to="/blog" className="text-[#f5f5f5]">Insights</Link>
              <Link to="/#approach" className="hover:text-[#f5f5f5] transition-colors">Our Approach</Link>
              <Link to="/#access" className="hover:text-[#f5f5f5] transition-colors">Advisory Access</Link>
              <Link
                to="/apply"
                className="px-5 py-2 text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #E8651A 0%, #F5922B 100%)' }}
              >
                Apply for Access
              </Link>
            </div>
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-[#888888] hover:text-[#f5f5f5]"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-[#222222] py-6 flex flex-col gap-4 text-sm font-medium">
              <Link to="/blog" className="text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Insights</Link>
              <Link to="/#approach" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Our Approach</Link>
              <Link to="/#access" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Advisory Access</Link>
              <Link
                to="/apply"
                className="bg-[#E8651A] text-[#0a0a0a] px-5 py-3 text-sm font-semibold text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Apply for Access
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Article Header */}
      <header className="border-b border-[#222222]">
        <div className="max-w-3xl mx-auto px-6 py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-[#555555] mb-8">
            <Link to="/blog" className="hover:text-[#E8651A] transition-colors">Insights</Link>
            <span>›</span>
            <span className="text-[#888888]">{post.category}</span>
          </nav>

          <div className="flex items-center gap-3 mb-5">
            <div style={{ width: 28, height: 3, background: 'linear-gradient(90deg, #E8651A, #F5922B)' }} />
            <p className="text-xs text-[#E8651A] uppercase tracking-widest font-semibold">{post.category}</p>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-6 tracking-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-xs text-[#555555]">
            <span className="text-[#888888] font-medium">{post.author}</span>
            <span>·</span>
            <span>
              {new Date(post.date).toLocaleDateString('en-ZA', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Excerpt */}
        <p className="text-lg text-[#888888] leading-relaxed mb-10 pb-10 border-b border-[#222222]">
          {post.excerpt}
        </p>

        {/* Article body */}
        <article
          className="article-body"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-[#222222] flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="border border-[#222222] text-[#555555] text-xs px-3 py-1 font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Author Block */}
        <div className="mt-10 border border-[#222222] p-8 flex flex-col sm:flex-row gap-6 items-start">
          <img 
            src="/douglas-bothma.jpg" 
            alt="Douglas Bothma" 
            className="w-16 h-16 rounded-full object-cover shrink-0"
          />
          <div>
            <p className="font-bold text-[#f5f5f5] mb-1">Douglas Bothma</p>
            <p className="text-[#E8651A] text-xs uppercase tracking-widest mb-3">Founder &amp; Senior Advisor — Custom Group</p>
            <p className="text-[#888888] text-sm leading-relaxed">
              Douglas advises leadership teams on AI capital allocation and implementation strategy.
              13+ years of enterprise software delivery, grounded in measurable economic outcome.
            </p>
            <a
              href="https://www.linkedin.com/in/douglasbothma/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-sm text-[#888888] hover:text-[#E8651A] transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              Connect on LinkedIn
            </a>
          </div>
        </div>

        {/* Advisory CTA */}
        <div
          className="mt-10 p-10 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(232,101,26,0.1) 0%, rgba(245,146,43,0.05) 100%)',
            border: '1px solid rgba(232,101,26,0.25)',
          }}
        >
          {/* Accent bar */}
          <div style={{ width: 32, height: 3, background: 'linear-gradient(90deg, #E8651A, #F5922B)', marginBottom: '1.25rem' }} />
          <h2 className="text-xl font-bold mb-3 tracking-tight">Advisory, not content.</h2>
          <p className="text-[#888888] text-sm mb-6 leading-relaxed">
            If your organisation is making AI investment decisions in the next 90 days, a working session
            with our senior advisory team will save you significantly more than it costs.
            Engagements begin at $7,500.
          </p>
          <Link
            to="/apply"
            className="inline-block px-8 py-3 text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #E8651A 0%, #F5922B 100%)' }}
          >
            Apply for Advisory Access
          </Link>
        </div>
      </main>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="border-t border-[#222222]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
            <h2 className="text-xl font-bold mb-10 tracking-tight">Related Insights</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#222222]">
              {related.map((p) => (
                <RelatedPostCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-[#222222] py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <img src="/logo.svg" alt="Custom Group.Tech" className="h-10 w-auto mb-3" />
            <p className="text-xs text-[#888888] italic mb-1">We think the future, your future</p>
            <p className="text-xs text-[#555555]">Ballito, South Africa · © 2026 Custom Group.Tech. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap gap-8 text-sm text-[#888888]">
            <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
            <Link to="/founder" className="hover:text-[#f5f5f5] transition-colors">About</Link>
            <Link to="/apply" className="hover:text-[#f5f5f5] transition-colors">Apply</Link>
            <Link to="/terms" className="hover:text-[#f5f5f5] transition-colors">Terms</Link>
            <a
              href="https://www.linkedin.com/in/douglasbothma/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#f5f5f5] transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
